import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthToken, getUserData, getUserType } from '../utils/authUtils';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getAllDashboardData } from '../services/dashboardService';
import { respondToInvitation, getInvitationThreadId, cancelInvitation } from '../services/invitationService';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import QuickActionsGrid from '../components/Dashboard/QuickActionsGrid';
import SessionCard from '../components/Dashboard/SessionCard';
import CancellationModal from '../components/Dashboard/CancellationModal';
import ActivitySummaryCard from '../components/Dashboard/ActivitySummaryCard';
import RecentConnectionsCard from '../components/Dashboard/RecentConnectionsCard';
import { Card, CardHeader, CardTitle, CardContent } from '../components/UI/card';
import { Button } from '../components/UI/button';
import { Avatar, AvatarFallback } from '../components/UI/avatar';
import { Calendar, Loader2, Check, X, Clock, DollarSign, Video, CheckCircle } from 'lucide-react';
import SessionCompletionModal from '../components/Dashboard/SessionCompletionModal';
import { markSessionCompleted } from '../services/sessionService';

// Define API_URL with explicit port 5000 to match the server
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
console.log('Dashboard API_URL initialized as:', API_URL);

const Dashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({ firstName: 'User', lastName: '', userType: 'professional' });
  const [userDisplayName, setUserDisplayName] = useState('User');
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [activityData, setActivityData] = useState({
    pendingRequests: 0,
    totalMatches: 0,
    completedMeetings: 0,
    profileViews: 0,
    totalSessions: 0,
    totalHours: 0
  });
  const [invitationStats, setInvitationStats] = useState({
    pending: 0,
    pendingReceived: 0,
    pendingSent: 0,
    accepted: 0,
    sent: 0
  });
  const [recentConnections, setRecentConnections] = useState([]);
  const [tokenStatus, setTokenStatus] = useState({ exists: false, value: null });

  // Cancellation modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedSessionForCancel, setSelectedSessionForCancel] = useState(null);

  // Session completion modal state
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [selectedSessionForCompletion, setSelectedSessionForCompletion] = useState(null);
  const [completionLoading, setCompletionLoading] = useState(false);

  // Debug state
  const [debugResult, setDebugResult] = useState(null);
  const theme = useTheme();
  const isProfessional = getUserType() === 'professional';

  const testApiConnection = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      console.log('Testing API connection to:', baseUrl);
      
      const response = await axios.get(`${baseUrl}/test`, {
        timeout: 5000
      });
      
      console.log('API Test Response:', response.data);
      setDebugResult({ success: true, data: response.data });
    } catch (error) {
      console.error('API Test Error:', error);
      setDebugResult({ success: false, error: error.message });
    }
  };

  // Function to fetch fresh user data from API
  const fetchFreshUserData = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        console.log('[Dashboard] No token available for API call');
        return null;
      }

      const baseUrl = getApiBaseUrl();
      const profileUrl = `${baseUrl}/auth/profile`;
      console.log('[Dashboard] 🔄 Fetching fresh user data from API:', profileUrl);

      const response = await axios.get(profileUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data && response.data.success) {
        const freshUserData = response.data.data;
        console.log('[Dashboard] ✅ Fresh user data fetched:', {
          name: freshUserData.name,
          firstName: freshUserData.firstName,
          lastName: freshUserData.lastName,
          email: freshUserData.email
        });

        // Update localStorage with fresh data
        localStorage.setItem('userData', JSON.stringify(freshUserData));
        console.log('[Dashboard] 💾 Updated localStorage with fresh API data');

        return freshUserData;
      } else {
        console.log('[Dashboard] ❌ API returned unsuccessful response:', response.data);
        return null;
      }
    } catch (error) {
      console.error('[Dashboard] ❌ Error fetching fresh user data:', error);
      return null;
    }
  }, []);

  const refreshUserDisplayName = useCallback(async () => {
    try {
      console.log('[Dashboard] 🔄 Refreshing user display name...');
      
      // First try to get fresh data from API
      const freshUserData = await fetchFreshUserData();
      let userData = freshUserData;
      
      // If API fails, fall back to localStorage
      if (!userData) {
        console.log('[Dashboard] 📱 API failed, falling back to localStorage');
        userData = getUserData();
      }
      
      let displayName = 'User'; // Default fallback
      
      if (userData) {
        console.log('[Dashboard] 📋 Processing user data for display name:', {
          preferredName: userData.preferredName,
          firstName: userData.firstName,
          name: userData.name,
          email: userData.email
        });
        
        // Enhanced priority order for display name:
        // 1. Preferred name (highest priority)
        // 2. First name
        // 3. Extract first name from full name
        // 4. Email username (fallback)
        // 5. "User" (final fallback)
        if (userData.preferredName && userData.preferredName.trim()) {
          displayName = userData.preferredName.trim();
        } else if (userData.firstName && userData.firstName.trim()) {
          displayName = userData.firstName.trim();
        } else if (userData.name && userData.name.trim()) {
          // Extract first name from full name
          displayName = userData.name.trim().split(' ')[0];
        } else if (userData.email && userData.email.trim()) {
          // Use email username as fallback (everything before @)
          displayName = userData.email.split('@')[0];
        } else {
          // Final fallback - use "User" instead of userType
          displayName = 'User';
        }
        
        console.log('[Dashboard] ✅ Final display name determined:', displayName);
      } else {
        console.log('[Dashboard] ⚠️ No user data available, using default');
      }
      
      setUserDisplayName(displayName);
      return displayName;
    } catch (error) {
      console.error('[Dashboard] ❌ Error refreshing display name:', error);
      setUserDisplayName('User');
      return 'User';
    }
  }, [fetchFreshUserData]);

  // Enhanced localStorage listener to detect changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'userData' || e.key === null) {
        console.log('[Dashboard] localStorage userData changed, refreshing display name');
        refreshUserDisplayName();
      }
    };

    // Listen for storage changes from other tabs/windows
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom storage events (same tab updates)
    const handleCustomStorageUpdate = (event) => {
      console.log('[Dashboard] 🔔 Profile update event received');
      console.log('[Dashboard] Event detail:', event.detail);
      
      // Immediately refresh display name from API (not just localStorage)
      console.log('[Dashboard] 🔄 Auto-refreshing display name due to profile update...');
      refreshUserDisplayName();
      
      // If the event contains profile data, update our local state too
      if (event.detail && (event.detail.firstName || event.detail.name || event.detail.preferredName)) {
        console.log('[Dashboard] 📝 Updating local user data from profile update event');
        
        // Extract the best display name using the same priority logic
        let firstName = 'User';
        if (event.detail.preferredName && event.detail.preferredName.trim()) {
          firstName = event.detail.preferredName.trim();
        } else if (event.detail.firstName && event.detail.firstName.trim()) {
          firstName = event.detail.firstName.trim();
        } else if (event.detail.name && event.detail.name.trim()) {
          firstName = event.detail.name.trim().split(' ')[0];
        }
        
        const lastName = event.detail.lastName || 
                        (event.detail.name ? event.detail.name.split(' ').slice(1).join(' ') : '') || 
                        '';
        
        console.log('[Dashboard] 📝 Updating local state with:', { firstName, lastName });
        
        setUserData(prev => ({
          ...prev,
          firstName: firstName,
          lastName: lastName
        }));
        
        // Also immediately update the display name without waiting for API
        setUserDisplayName(firstName);
      }
    };

    window.addEventListener('userDataUpdated', handleCustomStorageUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataUpdated', handleCustomStorageUpdate);
    };
  }, [refreshUserDisplayName]);

  // Enhanced useEffect with user data refresh and window focus listener
  useEffect(() => {
    console.log('Dashboard component loaded/remounted');
    
    // Always refresh display name when dashboard loads/remounts
    // This ensures we get fresh data when navigating back from profile
    setTimeout(() => {
      console.log('[Dashboard] 🔄 Auto-refreshing on component mount...');
      refreshUserDisplayName();
    }, 100);
    
    // Check if user is authenticated using our helper
    const token = getAuthToken();
    const isExplicitlyLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    console.log('Dashboard auth check:', { 
      hasToken: !!token, 
      isExplicitlyLoggedIn,
      isAuthenticated: !!(token || isExplicitlyLoggedIn),
      tokenPreview: token ? token.substring(0, 10) + '...' : 'none'
    });
    
    // Update token status state
    setTokenStatus({
      exists: !!token,
      value: token ? `${token.substring(0, 10)}...` : null
    });
    
    // IMPORTANT: Now require a token to be present, not just isLoggedIn flag
    if (!token) {
      console.log('No token found, redirecting to login');
      // Clear any potentially stale login state
      localStorage.removeItem('isLoggedIn');
      navigate('/login');
      return;
    } else {
      console.log('Authentication found, user can access dashboard');
    }

    // Get user data from localStorage
    try {
      const userDataJson = localStorage.getItem('userData');
      console.log('Dashboard userData from localStorage:', userDataJson ? 'Found user data' : 'No user data');
      
      if (userDataJson) {
        const parsedUserData = JSON.parse(userDataJson);
        console.log('Dashboard parsed userData:', parsedUserData ? 'Successfully parsed' : 'Parse failed');
        
        // Extract proper name from different possible fields
        const firstName = parsedUserData.firstName || 
                         parsedUserData.name?.split(' ')[0] || 
                         parsedUserData.email?.split('@')[0] || 
                         'User';
        const lastName = parsedUserData.lastName || 
                        (parsedUserData.name?.split(' ').slice(1).join(' ')) || 
                        '';
        
        setUserData({
          firstName: firstName,
          lastName: lastName,
          userType: parsedUserData.userType || parsedUserData.role || 'professional'
        });
      } else {
        // Try to get from auth context or API if localStorage is empty
        console.log('No localStorage data found');
        setUserData({
          firstName: 'User',
          lastName: '',
          userType: 'professional'
        });
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage', error);
      // Fallback to default values
      setUserData({
        firstName: 'User',
        lastName: '',
        userType: 'professional'
      });
    }
    
    // Refresh display name
    refreshUserDisplayName();
    
    setLoading(false);
    
    // Fetch upcoming meetings and activity data
    fetchDashboardData();

    // Add window focus listener to refresh data when user returns
    const handleWindowFocus = () => {
      console.log('[Dashboard] Window focused, refreshing user data');
      refreshUserDisplayName();
      fetchDashboardData();
    };

    window.addEventListener('focus', handleWindowFocus);
    
    // Cleanup
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [navigate, refreshUserDisplayName]);

  // Handle cancel confirmation
  const handleConfirmCancellation = async (invitationId, reason) => {
    try {
      await cancelInvitation(invitationId, reason);
      toast.success('Session cancelled successfully. Refund will be processed within 5-10 business days.');

      // Refresh dashboard data
      await fetchDashboardData();

      // Close modal
      setCancelModalOpen(false);
      setSelectedSessionForCancel(null);
    } catch (error) {
      console.error('Cancellation failed:', error);
      // Error will be shown in the modal
      throw error;
    }
  };

  // Handle marking session as completed
  const handleMarkSessionComplete = (invitation) => {
    console.log('📋 Opening completion modal for invitation:', invitation._id);
    setSelectedSessionForCompletion(invitation);
    setCompletionModalOpen(true);
  };

  // Confirm session completion and capture payment
  const handleConfirmCompletion = async (notes) => {
    if (!selectedSessionForCompletion) return;

    setCompletionLoading(true);

    try {
      console.log('🎯 Marking session as completed:', {
        invitationId: selectedSessionForCompletion._id,
        notes: notes || 'None'
      });

      const result = await markSessionCompleted(
        selectedSessionForCompletion._id,
        notes
      );

      console.log('✅ Session completion response:', result);

      if (result.success) {
        toast.success(`Session completed! Payment of £${(result.data.paymentAmount / 100).toFixed(2)} captured.`, {
          description: `Your earnings: £${(result.data.professionalEarnings / 100).toFixed(2)} will be in your next payout.`,
          duration: 6000
        });

        // Close modal
        setCompletionModalOpen(false);
        setSelectedSessionForCompletion(null);

        // Refresh dashboard data
        await fetchDashboardData();
      } else {
        toast.error('Failed to complete session', {
          description: result.message || 'Please try again later'
        });
      }
    } catch (error) {
      console.error('❌ Error completing session:', error);

      // Handle specific error codes
      if (error.code === 'FORBIDDEN') {
        toast.error('Only the professional can mark sessions as completed');
      } else if (error.code === 'ALREADY_COMPLETED') {
        toast.error('This session has already been completed');
      } else if (error.code === 'PAYMENT_EXPIRED') {
        toast.error('Payment authorization has expired (>7 days)');
      } else if (error.code === 'NO_PAYMENT') {
        toast.error('No authorized payment found for this session');
      } else {
        toast.error('Failed to complete session', {
          description: error.message || 'Please try again later'
        });
      }
    } finally {
      setCompletionLoading(false);
    }
  };

  // Close completion modal
  const handleCloseCompletionModal = () => {
    if (!completionLoading) {
      setCompletionModalOpen(false);
      setSelectedSessionForCompletion(null);
    }
  };

  const fetchDashboardData = async () => {
    setDataLoading(true);

    try {
      const token = getAuthToken();
      if (!token) {
        console.warn('No auth token found for API requests');

        // Check token state again and update
        setTokenStatus({
          exists: false,
          value: null
        });

        throw new Error('No auth token found');
      }

      // Update token status
      setTokenStatus({
        exists: true,
        value: token.substring(0, 10) + '...'
      });

      // Get the base URL with /api prefix
      const baseUrl = getApiBaseUrl();
      console.log('Using baseUrl from centralized config:', baseUrl);

      // OPTIMIZED: Fetch all dashboard data in a single optimized call
      // This reduces API calls from 11+ down to 5 by eliminating duplicates
      console.log('Fetching all dashboard data (optimized)');
      const allData = await getAllDashboardData();

      if (allData.success) {
        // Set activity data
        setActivityData(allData.dashboardStats);
        console.log('Dashboard stats loaded:', allData.dashboardStats);

        // Set invitation stats
        setInvitationStats({
          pending: allData.invitationStats.pendingReceived,
          pendingReceived: allData.invitationStats.pendingReceived,
          pendingSent: allData.invitationStats.pendingSent,
          accepted: allData.invitationStats.accepted,
          sent: allData.invitationStats.pendingSent
        });
        console.log('Invitation stats loaded:', allData.invitationStats);

        // Process pending invitations from cached data
        const pendingInvitations = allData.invitations.pendingReceived || [];
        const validPendingInvitations = pendingInvitations.filter(inv => {
          return inv.sender && (inv.sender._id || inv.sender.firstName || inv.sender.lastName || inv.sender.name);
        });
        setPendingInvitations(validPendingInvitations);
        console.log('Pending invitations loaded:', validPendingInvitations.length, '(filtered from', pendingInvitations.length, 'total)');

        // Process accepted invitations for upcoming meetings from cached data
        const acceptedInvitations = allData.invitations.accepted || [];

        // Remove duplicates by invitation ID (in case API returns duplicates)
        const uniqueInvitations = acceptedInvitations.filter((inv, index, self) =>
          index === self.findIndex((t) => t._id === inv._id)
        );

        // Filter for invitations that have not been completed yet and not cancelled
        const upcomingInvitations = uniqueInvitations.filter(inv => {
          // Exclude cancelled invitations
          if (inv.status === 'cancelled') {
            return false;
          }
          // Check if there's a valid proposed date in sessionDetails
          if (inv.sessionDetails?.proposedDate) {
            const sessionTime = new Date(inv.sessionDetails.proposedDate);
            return sessionTime > new Date();
          }
          // If no date set, don't include it in upcoming meetings
          return false;
        });

        setUpcomingMeetings(upcomingInvitations);
        console.log('Confirmed coffee chats loaded:', upcomingInvitations.length);

        // Process recent connections from accepted invitations (cached data)
        const connections = acceptedInvitations
          .slice(0, 5)
          .map(inv => {
            const userId = getUserData()?._id;
            // Get the other person in the invitation
            const otherPerson = inv.senderId?._id === userId ? inv.recipientId : inv.senderId;
            return {
              ...otherPerson,
              connectedAt: inv.respondedAt || inv.createdAt
            };
          })
          .filter(conn => conn && conn._id); // Filter out null/undefined

        setRecentConnections(connections);
        console.log('Recent connections loaded:', connections.length);
      } else {
        console.error('Error in dashboard data:', allData.error);
        // Set default values if API fails
        setActivityData({
          pendingRequests: 0,
          totalMatches: 0,
          completedMeetings: 0,
          profileViews: 0,
          totalSessions: 0,
          totalHours: 0
        });
        setPendingInvitations([]);
        setUpcomingMeetings([]);
        setRecentConnections([]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set default values on error
      setActivityData({
        pendingRequests: 0,
        totalMatches: 0,
        completedMeetings: 0,
        profileViews: 0,
        totalSessions: 0,
        totalHours: 0
      });
      setPendingInvitations([]);
      setUpcomingMeetings([]);
      setRecentConnections([]);
    } finally {
      setDataLoading(false);
    }
  };

  // Add a manual logout function
  const handleLogout = () => {
    console.log('Manual logout requested');
    // Clear all auth data
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('userId');
    localStorage.removeItem('isLoggedIn');
    // Force navigation to login
    navigate('/login');
  };

  // Handle confirming an invitation
  const handleConfirmInvitation = async (invitationId) => {
    try {
      await respondToInvitation(invitationId, { status: 'accepted' });
      toast.success('Session confirmed!');
      // Remove from pending invitations
      setPendingInvitations(prev => prev.filter(inv => inv._id !== invitationId));
      // Refresh dashboard data
      fetchDashboardData();
    } catch (error) {
      console.error('Error confirming invitation:', error);
      toast.error(error.message || 'Failed to confirm invitation');
    }
  };

  // Handle declining an invitation
  const handleDeclineInvitation = async (invitationId) => {
    try {
      await respondToInvitation(invitationId, { status: 'declined' });
      toast.info('Session declined');
      // Remove from pending invitations
      setPendingInvitations(prev => prev.filter(inv => inv._id !== invitationId));
      // Refresh dashboard data
      fetchDashboardData();
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast.error(error.message || 'Failed to decline invitation');
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ padding: '2rem', textAlign: 'center' }}>
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ padding: '32px 16px' }}>
      <div className="mx-auto" style={{ maxWidth: '1280px' }}>


        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '12px' }}>
            <h1 className="text-3xl font-normal text-gray-900 dark:text-white" style={{ margin: 0 }}>
              Welcome back, {userDisplayName}!
            </h1>
          </div>
          <p className="text-base text-gray-600 dark:text-gray-400" style={{ margin: 0 }}>
            Here's what's happening with your GrabACoffee account
          </p>
        </div>

      {/* Quick Actions Grid - Horizontal Layout */}
      <QuickActionsGrid isProfessional={isProfessional} />

      {/* Main Content Grid - 3 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Left 2 Columns - Sessions */}
        <div className="lg:col-span-2 space-y-6">
          {/* Confirmed Coffee Chats - No Card wrapper */}
          <div>
            <h2 className="text-2xl mb-4 text-gray-900 dark:text-white">Confirmed Coffee Chats</h2>
            {dataLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : upcomingMeetings.length > 0 ? (
              <div className="space-y-4">
                {upcomingMeetings.slice(0, 3).map((invitation) => {
                  const userId = getUserData()?._id;
                  const otherPerson = invitation.sender?._id === userId
                    ? invitation.receiver
                    : invitation.sender;
                  const sessionDetails = invitation.sessionDetails || {};
                  const proposedDate = sessionDetails.proposedDate ? new Date(sessionDetails.proposedDate) : null;

                  // Check if current user is the sender (initiated the invitation)
                  const isInvitationSender = invitation.sender?._id === userId;

                  const otherPersonName = otherPerson?.name || `${otherPerson?.firstName || ''} ${otherPerson?.lastName || ''}`.trim() || 'User';
                  const initials = otherPersonName.split(' ').map(n => n[0]).join('').toUpperCase();
                  const headline = otherPerson?.headline || otherPerson?.title || 'Professional';

                  const handleJoinChat = async () => {
                    try {
                      const result = await getInvitationThreadId(invitation._id);
                      if (result.success && result.threadId) {
                        navigate(`/messages?thread=${result.threadId}`);
                      } else {
                        toast.error('Could not find chat thread');
                      }
                    } catch (error) {
                      console.error('Error getting thread ID:', error);
                      toast.error('Failed to open chat');
                    }
                  };

                  const handleJoinMeeting = (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const meetingLink = invitation.sessionDetails?.meetingLocation?.virtualLink;
                    console.log('🔍 Join Meeting clicked');
                    console.log('🔍 Raw meeting link:', meetingLink);
                    console.log('🔍 Full invitation object:', invitation);
                    console.log('🔍 Session details:', invitation.sessionDetails);
                    console.log('🔍 Meeting location:', invitation.sessionDetails?.meetingLocation);

                    if (meetingLink) {
                      // Ensure the link has a protocol
                      let fullLink = meetingLink;
                      if (!meetingLink.startsWith('http://') && !meetingLink.startsWith('https://')) {
                        fullLink = 'https://' + meetingLink;
                        console.log('⚠️ Added https:// prefix, new link:', fullLink);
                      }

                      console.log('✅ Opening link:', fullLink);
                      window.open(fullLink, '_blank', 'noopener,noreferrer');
                    } else {
                      console.error('❌ No meeting link found!');
                      alert('No meeting link available');
                    }
                  };

                  const handleCancelClick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedSessionForCancel(invitation);
                    setCancelModalOpen(true);
                  };

                  const isVirtualMeeting = invitation.sessionDetails?.meetingLocation?.type === 'virtual';
                  const hasMeetingLink = invitation.sessionDetails?.meetingLocation?.virtualLink;

                  console.log('Invitation:', invitation._id, {
                    isVirtualMeeting,
                    hasMeetingLink,
                    meetingLocation: invitation.sessionDetails?.meetingLocation
                  });

                  return (
                    <Card key={invitation._id}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-blue-600 text-white">
                              {initials}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h3 className="text-lg text-gray-900 dark:text-white">{otherPersonName}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{headline}</p>
                            <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600 dark:text-gray-400">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {proposedDate
                                  ? new Date(proposedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                  : 'Date TBD'}
                              </div>
                              {proposedDate && (
                                <div className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  {new Date(proposedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  {sessionDetails.duration && ` (${sessionDetails.duration} min)`}
                                </div>
                              )}
                              {sessionDetails.amount && (
                                <div className="flex items-center gap-1">
                                  <DollarSign className="h-4 w-4" />
                                  ${sessionDetails.amount}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <div className="flex gap-2">
                              {isVirtualMeeting && hasMeetingLink && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={handleJoinMeeting}
                                >
                                  <Video className="h-4 w-4 mr-1" />
                                  Join Meeting
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleJoinChat}
                              >
                                Messages
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleCancelClick}
                                className="border-orange-500 text-orange-600 hover:bg-orange-50 dark:border-orange-600 dark:text-orange-500 dark:hover:bg-orange-950"
                              >
                                Cancel
                              </Button>
                            </div>
                            {/* Mark as Completed button (Professional only, not for invitation senders) */}
                            {isProfessional &&
                             !isInvitationSender &&
                             (invitation.status === 'payment_authorized' || invitation.status === 'accepted') &&
                             invitation.status !== 'completed' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleMarkSessionComplete(invitation);
                                }}
                                className="bg-white border-green-500 text-green-600 hover:bg-green-50 dark:border-green-600 dark:text-green-500 dark:hover:bg-green-950 font-semibold"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Mark as Completed
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-muted-foreground mb-2">You have no confirmed coffee chats yet</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Coffee chats appear here once both parties confirm the meeting details
                  </p>
                  <Button
                    onClick={() => navigate('/professionals')}
                  >
                    Find Professionals
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Pending Confirmation - No Card wrapper */}
          {pendingInvitations.length > 0 && (
            <div>
              <h2 className="text-2xl mb-4 text-gray-900 dark:text-white">Pending Confirmation</h2>
              <div className="space-y-4">
                {pendingInvitations.slice(0, 3).map((invitation) => {
                  const sender = invitation.sender;
                  const sessionDetails = invitation.sessionDetails || {};
                  const proposedDate = sessionDetails.proposedDate ? new Date(sessionDetails.proposedDate) : null;

                  const senderName = sender?.name || `${sender?.firstName || ''} ${sender?.lastName || ''}`.trim() || 'User';
                  const initials = senderName.split(' ').map(n => n[0]).join('').toUpperCase();
                  const headline = sender?.headline || 'Professional';

                  return (
                    <Card key={invitation._id} className="border-orange-200 dark:border-orange-800">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-orange-600 text-white">
                              {initials}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h3 className="text-lg text-gray-900 dark:text-white">{senderName}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{headline}</p>
                            <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600 dark:text-gray-400">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {proposedDate
                                  ? new Date(proposedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                  : 'Date TBD'}
                              </div>
                              {proposedDate && (
                                <div className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  {new Date(proposedDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  {sessionDetails.duration && ` (${sessionDetails.duration} min)`}
                                </div>
                              )}
                              {sessionDetails.amount && (
                                <div className="flex items-center gap-1">
                                  <DollarSign className="h-4 w-4" />
                                  ${sessionDetails.amount}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 mt-4">
                              <Button
                                size="sm"
                                onClick={() => handleConfirmInvitation(invitation._id)}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeclineInvitation(invitation._id)}
                              >
                                <X className="h-4 w-4 mr-1" />
                                Decline
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Activity Summary */}
        <div>
          <h2 className="text-2xl mb-4 text-gray-900 dark:text-white">Activity Summary</h2>
          <div className="space-y-4">
            <ActivitySummaryCard
              sessions={activityData.completedMeetings || 0}
              totalHours={activityData.totalHours || 0}
              totalInteractions={invitationStats.accepted || 0}
            />
            <RecentConnectionsCard connections={recentConnections} />
          </div>
        </div>
      </div>
      </div>

      {/* Cancellation Modal */}
      <CancellationModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        session={selectedSessionForCancel}
        userRole={isProfessional ? 'Professional' : 'Seeker'}
        onConfirm={handleConfirmCancellation}
      />

      {/* Session Completion Modal */}
      <SessionCompletionModal
        open={completionModalOpen}
        onClose={handleCloseCompletionModal}
        session={selectedSessionForCompletion}
        onConfirm={handleConfirmCompletion}
        loading={completionLoading}
      />
    </div>
  );
};

export default Dashboard; 